import { EventMilestoneValidationSchema } from "@/event/validation/milestoneSchemas";
import { FrameName } from "@/game/entities/config";
import { AchievementStates } from "@/game/entities/config/achievements/achievement_states";
import { QuestObjectiveActions } from "@/game/entities/config/quests/quest_objective_actions";
import { QuestRewardTypes } from "@/game/entities/config/quests/quest_reward_types";
import { QuestTypes } from "@/game/entities/config/quests/quest_types";
import { ResourceType } from "@/game/entities/config/resources";
import { settlementNames } from "@/game/entities/config/settlements";
import { UpgradeIds } from "@/game/entities/config/upgrades";
import { type SettlementKey } from "@/store/useSettlementsStore";
import { SiegeSystemResult } from "@/store/useSiegeSystemStore";
import {
	COINS_QUEST_REWARD_CONST,
	EXPLORATION_SPEED_CONST,
} from "@/types/quest";
import { type TileCoordinates } from "@/types/tile";
import {
	EventCategory,
	EventStatus,
	GraphicDetails,
	IconTypes,
} from "@/types/ui";
import {
	array,
	boolean,
	custom,
	date,
	email,
	entriesFromList,
	enum_,
	nullable,
	number,
	object,
	optional,
	partial,
	picklist,
	pipe,
	record,
	string,
	union,
	uuid,
	type BooleanSchema,
	type NumberSchema,
	type StringSchema,
} from "valibot";

type BuildingsStorageStoreAccumulator = Record<
	FrameName,
	NumberSchema<undefined>
>;

type SettlementsStoreAccumulator = Record<
	SettlementKey,
	Record<
		string,
		| BooleanSchema<undefined>
		| NumberSchema<undefined>
		| StringSchema<undefined>
	>
>;

const ResourcesValidationSchema = partial(
	object(
		entriesFromList(
			[
				"coins",
				"gems",
				"lumber",
				"stone",
				"clay",
				"iron",
				"goods",
				"steel",
				"army",
				"rations",
				"foodDiff",
				"populationDiff",
				"superClicker",
				"clicker2",
				"clicker3",
				"clicker5",
				"adSkipper",
				"explorationSpeed",
				"coinsIncome",
				"coinsPlus",
				"coinsMinus",
				"gemsPlus",
				"gemsMinus",
				"clicker2Plus",
				"clicker2Minus",
				"clicker3Plus",
				"clicker3Minus",
				"clicker5Plus",
				"clicker5Minus",
				"superClickerPlus",
				"superClickerMinus",
				"adSkipperPlus",
				"adSkipperMinus",
				"adsWatchedTotal",
				"ticksTotal",
				"clicksTotal",
				"clicksCastleTotal",
				"clicksExplorationTotal",
				"eventium",
				"eventiumIncome",
				"eventiumUncappedIncome",
				"eventiumUncappedIncomeWithoutPenalties",
				"eventiumAccumulated",
				"eventiumConsumption",
				"eventiumPlus",
				"eventiumMinus",
			],
			number(),
		),
	),
);

export const LanguesListValidationSchema = object({
	languages: array(
		object({
			code: string(),
			name: string(),
			percent_completed: number(),
		}),
	),
});

export const TranslationsRefetchedValidationSchema = object({
	data: record(string(), string()),
	timestamp: number(),
});

export const TranslationsNotChangedValidationSchema = object({
	timestamp: number(),
});

export const TranslationsFetchFailedValidationSchema = object({
	message: string(),
	code: string(),
});

export const LocalUserIdResponseValidationSchema = object({
	data: object({
		registerOrLoginDevice: object({
			accessToken: string(),
			deviceSecret: string(),
			userId: string(),
		}),
	}),
	errors: optional(array(object({ message: string() }))),
});

export const LocalTokenResponseValidationSchema = object({
	data: object({
		token: object({
			token: string(),
			expiresAt: optional(number()),
		}),
	}),
	errors: optional(array(object({ message: string() }))),
});

export const DeleteUserResultValidationSchema = object({
	status: boolean(),
});

export const GetUserByIdResultValidationSchema = object({
	status: boolean(),
	state: optional(
		object({
			id: string(),
			name: string(),
			level: number(),
			coins: number(),
			tilesExplored: number(),
		}),
	),
});

const Leaderboard = array(
	object({
		placement: number(),
		user_name: string(),
		country: string(),
		score: number(),
		country_placement: number(),
	}),
);

export const GetScoreboardResultValidationSchema = object({
	status: boolean(),
	state: optional(
		object({
			globalTop20: Leaderboard,
			globalAroundPlayer: Leaderboard,
			countryTop20: Leaderboard,
			countryAroundPlayer: Leaderboard,
		}),
	),
});

export const ChangeNameResultValidationSchema = object({
	status: boolean(),
	reason: optional(string()),
});

export const LoadResponseValidationSchema = object({
	status: boolean(),
	data: optional(
		object({
			map: optional(
				object({
					tiles: array(
						object({
							tile: object({
								tileIndex: number(),
								tileIds: array(number()),
							}),
							params: object({
								visibility: pipe(
									number(),
									custom((value: unknown): boolean =>
										typeof value === "number"
											? [0, 1, 2].includes(value)
											: false,
									),
								),
								durability: number(),
								isConnectedToMainSettlement: pipe(
									number(),
									custom((value: unknown): boolean =>
										typeof value === "number"
											? [0, 1].includes(value)
											: false,
									),
								),
								isBlockedByEnemy: pipe(
									number(),
									custom((value: unknown): boolean =>
										typeof value === "number"
											? [0, 1].includes(value)
											: false,
									),
								),
							}),
						}),
					),
				}),
			),
			mapExpansion: optional(
				record(
					string(),
					object({
						isUnlocked: boolean(),
						cache: optional(
							array(
								object({
									target: optional(number()),
									current: optional(number()),
								}),
							),
						),
					}),
				),
			),
			resources: optional(ResourcesValidationSchema),
			buildingsStorage: optional(
				partial(
					object(
						(Object.values(FrameName) as Array<FrameName>).reduce(
							(
								acc: BuildingsStorageStoreAccumulator,
								frameName: FrameName,
							): BuildingsStorageStoreAccumulator => ({
								...acc,
								[frameName]: number(),
							}),
							{} as BuildingsStorageStoreAccumulator,
						),
					),
				),
			),
			settlements: optional(
				partial(
					object(
						// @ts-expect-error - TS2345
						(
							(
								Object.values(
									settlementNames,
								) as Array<TileCoordinates>
							).map(
								({ x, y }: TileCoordinates): SettlementKey =>
									`${x}|${y}`,
							) as Array<SettlementKey>
						).reduce(
							(
								acc: SettlementsStoreAccumulator,
								key: SettlementKey,
							): SettlementsStoreAccumulator => ({
								...acc,
								[key]: object({
									name: string(),
									level: number(),
									isQuestSkipEnabled: boolean(),
									availableClicks: number(),
									totalClicks: number(),
									wasRevealed: boolean(),
									wasActivated: boolean(),
								}),
							}),
							{} as SettlementsStoreAccumulator,
						),
					),
				),
			),
			upgrades: optional(
				record(
					// enum_(UpgradeIds) - toto nejde, lebo tam je typ number, ktory sa to automaticky v objekte prehodi na string
					pipe(
						string(),
						custom(
							(value: unknown): boolean =>
								typeof value === "string" && !isNaN(+value),
						),
					),
					number(),
				),
			),
			quests: optional(
				partial(
					object({
						activeQuests: array(
							object({
								type: enum_(QuestTypes),
								title: string(),
								flavor: string(),
								assignment: string(),
								objective: object({
									action: enum_(QuestObjectiveActions),
									amount: number(),
									type: optional(
										union([
											enum_(FrameName),
											array(enum_(FrameName)),
											enum_(ResourceType),
										]),
									),
									pay: optional(number()),
									coordination: optional(
										object({
											x: number(),
											y: number(),
										}),
									),
									settlement_id: optional(number()),
									level: optional(number()),
								}),
								reward: object({
									type: enum_(QuestRewardTypes),
									content: array(
										object({
											reward_resource: union([
												enum_(ResourceType),
												enum_(FrameName),
												pipe(
													string(),
													custom(
														(
															value: unknown,
														): boolean =>
															typeof value ===
															"string"
																? (
																		[
																			COINS_QUEST_REWARD_CONST,
																			EXPLORATION_SPEED_CONST,
																		] as Array<string>
																	).includes(
																		value,
																	)
																: false,
													),
												),
											]),
											amount: number(),
										}),
									),
								}),
								id: number(),
								progress: number(),
								payed: optional(number()),
								started_at: optional(string()),
							}),
						),
						finishedQuests: array(
							union([
								number(),
								object({
									id: number(),
									started_at: optional(string()),
									completed_at: optional(string()),
								}),
								object({
									type: enum_(QuestTypes),
									title: string(),
									flavor: string(),
									assignment: string(),
									objective: object({
										action: enum_(QuestObjectiveActions),
										amount: number(),
										type: optional(
											union([
												enum_(FrameName),
												array(enum_(FrameName)),
												enum_(ResourceType),
											]),
										),
										pay: optional(number()),
										coordination: optional(
											object({
												x: number(),
												y: number(),
											}),
										),
										settlement_id: optional(number()),
										level: optional(number()),
									}),
									reward: object({
										type: enum_(QuestRewardTypes),
										content: array(
											object({
												reward_resource: union([
													enum_(ResourceType),
													enum_(FrameName),
													pipe(
														string(),
														custom(
															(
																value: unknown,
															): boolean =>
																typeof value ===
																"string"
																	? (
																			[
																				COINS_QUEST_REWARD_CONST,
																				EXPLORATION_SPEED_CONST,
																			] as Array<string>
																		).includes(
																			value,
																		)
																	: false,
														),
													),
												]),
												amount: number(),
											}),
										),
									}),
									id: number(),
									progress: number(),
									payed: optional(number()),
								}),
							]),
						),
						rewardsByTick: ResourcesValidationSchema,
						activeNarrativeId: nullable(number()),
						activeNarrativeChapter: nullable(number()),
						tutorialActive: boolean(),
						tutorialStep: nullable(number()),
					}),
				),
			),
			achievements: optional(
				partial(
					object({
						achievements: array(
							object({
								id: number(),
								progress: number(),
								state: enum_(AchievementStates),
							}),
						),
						hideClaimed: boolean(),
						lastUnlockedTitle: string(),
					}),
				),
			),
			settings: optional(
				partial(
					object({
						graphicDetails: enum_(GraphicDetails),
						animationsOn: boolean(),
						musicOn: boolean(),
						soundsOn: boolean(),
						musicVolume: number(),
						soundsVolume: number(),
						showTownLevels: boolean(),
						showMainSettlementNameLabel: boolean(),
						showConfirmGemsMessages: boolean(),
						showConfirmGemsMessagesUpgrades: boolean(),
						showConfirmGemsMessagesShop: boolean(),
						showIncomePanel: boolean(),
						showExplorationSpeed: boolean(),
						showGameSavedMessage: boolean(),
						showExplorationProgress: boolean(),
						showMenuTexts: boolean(),
						tileDetailGridView: boolean(),
						cameraSave: object({
							x: number(),
							y: number(),
							zoomLevel: number(),
						}),
						showArrowsToSettlementSwitch: boolean(),
					}),
				),
			),
			user: optional(
				partial(
					object({
						userName: string(),
						userEmail: pipe(string(), email()),
						userId: pipe(string(), uuid()),
						country: pipe(
							string(),
							custom(
								(value: unknown): boolean =>
									typeof value === "string" &&
									[2, 3].includes(value.length),
							),
						),
						language: pipe(
							string(),
							custom(
								(value: unknown): boolean =>
									typeof value === "string" &&
									value.length === 2,
							),
						),
						linkedEmail: boolean(),
						linkedSocialMediaType: array(
							picklist(["apple", "google"]),
						),
						playerLevel: number(),
						townsConquered: number(),
						townsDiscovered: number(),
						totalTownLevels: number(),
						totalWorkforce: number(),
						totalFoodProduction: number(),
						tilesExplored: number(),
						forestsOnExploredTiles: number(),
						exploredWaterTiles: number(),
						roadsAndBridges: number(),
						completedUpgrades: number(),
						superClickersUsed: number(),
						accountEstablished: union([number(), string(), date()]), // TODO: string() - ale aj v storku
						lastLogin: union([number(), string(), date()]), // TODO: string() - ale aj v storku
						lastTickBefore: number(),
						maxOfflineTime: number(),
						offlinePercentage: number(),
						showPrivacyOptionsSettings: boolean(),
						newUpgradesIds: array(enum_(UpgradeIds)),
						inGameGemPurchaseForCoinsOfferId: number(),
						appRated: boolean(),
						appRatedGemRewardClaimed: boolean(),
						discordRewardClaimed: boolean(),
						upgradeFilterVisibility: pipe(
							string(),
							custom(
								(value: unknown): boolean =>
									typeof value === "string" &&
									[IconTypes.HIDE, IconTypes.SHOW].includes(
										value as IconTypes,
									),
							),
						),
						gemMinesClaimable: boolean(),
						lastSeenNewsTimestamp: nullable(
							union([number(), string(), date()]), // TODO: string() - ale aj v storku
						),
						lastDailyRewardPickedDaysAfterReg: number(),
						daysAfterRegistration: number(),
						nameChanged: boolean(),
						marketplaceTimestamps: record(string(), number()),
						upgrade6NerfFixedv2: boolean(),
						appliedUpgradeRetrogrants: array(string()),
						incomeBoostsUsedToday: number(),
						incomeBoostTicksRemaining: number(),
						activeSpecialLocationsIds: array(number()),
						revealedSettlementFromOfferCoordinates: nullable(
							object({
								x: number(),
								y: number(),
							}),
						),
						isPayingCustomer: boolean(),
						isDailyFreeEventPackClaimed: nullable(
							union([number(), string(), date()]), // TODO: string() - ale aj v storku
						),
						adRewardsCount: number(),
						appliedEventGeneration: optional(number()),
						last_tick_hash: string(),
						plagueCoordinates: nullable(
							array(
								object({
									coordinates: object({
										x: number(),
										y: number(),
									}),
									maxHp: number(),
									currentHp: number(),
								}),
							),
						),
					}),
				),
			),
			// TODO: zakomentovane, lebo je tato schema nespravna (chyba ID z BE, datumy su posielane ako string)
			// flashNews: optional(
			// 	array(
			// 		object({
			// 			id: string(),
			// 			title: string(),
			// 			message: string(),
			// 			start_date: date(),
			// 			end_date: date(),
			// 			created_at: date(),
			// 		}),
			// 	),
			// ),
			/*
			// TODO: @DS: overit, ci to je spravne
			claimableRewards: optional(
				array(
					object({
						_id: pipe(string()),
						pack: object({
							name: string(),
							store_id: string(),
							attributes: object({
								type: string(),
								template: enum_(OfferTemplate),
								slots: array(
									object({
										items: array(
											object({
												group: enum_(CommodityGroup),
												frameName: optional(
													enum_(FrameName),
												),
												upgradeId: optional(
													enum_(UpgradeIds),
												),
												key: optional(
													ResourcesValidationSchema,
												),
												qty: number(),
											}),
										),
										text: optional(string()),
									}),
								),
							}),
							promo_bonus_percent: optional(number()),
						}),
					}),
				),
			),
			*/
			siegeSystem: optional(
				object({
					stats: object({
						startedAt: nullable(number()),
						endedAt: nullable(number()),
						damageDealt: number(),
						damageTaken: number(),
						enemiesDefeated: number(),
						enemiesTotal: number(),
					}),
					result: nullable(enum_(SiegeSystemResult)),
					siegeTargetTileIndexes: array(number()),
					mainEnemyTileIndex: nullable(number()),
					units: record(
						string(),
						object({
							hp: number(),
						}),
					),
					attackClicks: number(),
					movingUnitsIndex: nullable(number()),
				}),
			),
			maintenanceMode: boolean(),

			// SHAME! SHAME! SHAME!
			// TODO: quickTool
			// TODO: chest
			// TODO: packs
			// TODO: offer
			// TODO: eventOverview
		}),
	),
	reason: optional(string()),
});

export const SaveResponseValidationSchema = object({
	status: boolean(),
	data: optional(
		object({
			maintenanceMode: boolean(),
			event: nullable(
				object({
					id: string(),
					start_date: string(),
					end_date: string(),
					active: boolean(),
					status: enum_(EventStatus),
					attributes: object({
						type: nullable(enum_(EventCategory)),
						level_unlock: optional(number()),
						has_super_clicker_offer: optional(boolean()),
						event_pass_store_id: optional(nullable(string())),
						milestones: optional(
							nullable(array(EventMilestoneValidationSchema)),
						),
					}),
				}),
			),
			eventStatus: optional(enum_(EventStatus)),
			appliedEventGeneration: optional(number()),
		}),
	),
	reason: optional(string()),
});

export const StateResponseValidationSchema = object({
	status: boolean(),
	data: object({
		maintenanceMode: boolean(),
	}),
});

export const ClaimableRewardsLoadResponseValidationSchema = object({
	status: boolean(),
	state: optional(
		array(
			object({
				name: string(),
				store_id: string(),
				attributes: object({
					type: string(),
					value: optional(ResourcesValidationSchema),
					bonus: optional(ResourcesValidationSchema),
					rewards: optional(ResourcesValidationSchema),
				}),
			}),
		),
	),
});

export const OfferAnalyticsSaveResultValidationSchema = object({
	status: boolean(),
});
export const AdAnalyticsSaveResultValidationSchema = object({
	status: boolean(),
});

export const ChestStoreLoadValidationSchema = object({
	status: boolean(),
});
export const ChestStoreSaveResultValidationSchema = object({
	status: boolean(),
});

export const InGameCompensationRewardsLoadResponseValidationSchema = object({
	status: boolean(),
	inGameRewards: array(
		object({
			_id: string(),
			user_id: string(),
			rewards: object({
				type: string(),
				amount: number(),
				currency: optional(string()),
				upgradeId: optional(number()),
				frameName: optional(string()),
			}),
			source: string(),
			claimed: boolean(),
			message: optional(string()),
		}),
	),
});
